<template>
  <BaseDialogForm
    v-model="showItemForm"
    :title="editMode ? `Edit ${itemModule.item}` : `Add ${itemModule.item}`"
    :nonFieldErrors="formErrors.non_field_errors"
  >
    <template #content>
      <v-form
        v-model="isValid"
        ref="itemForm"
        :key="formCount"
        @submit.prevent="submitForm"
      >
        <v-row class="ma-0">
          <v-col cols="12" lg="7">
            <v-card outlined class="pa-6 rounded-lg h-100">
              <v-row>
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold">
                    Basic Information
                  </span>
                </v-col>
                <v-col cols="12" lg="6">
                  <InputField
                    id="item_no"
                    v-model="itemDetails.item_no"
                    label="Item No"
                    name="item_no"
                    :required="true"
                    :error-messages="formErrors.item_no"
                    @input="delete formErrors.item_no"
                  />
                </v-col>
                <v-col cols="12" lg="6">
                  <SelectField
                    id="storage_type"
                    v-model="itemDetails.storage_types"
                    label="Storage type*"
                    name="storage_type"
                    :itemsList="storageType"
                    :rules="[(val) => !!val || 'Storage type is required']"
                    hide-details="auto"
                    item-text="name"
                    item-value="id"
                    :error-messages="formErrors.storage_types"
                    @input="delete formErrors.storage_types"
                  />
                </v-col>
                <v-col cols="12">
                  <InputField
                    id="description"
                    v-model="itemDetails.description"
                    label="Description"
                    name="description"
                    :required="true"
                    :error-messages="formErrors.description"
                    @input="delete formErrors.description"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12" lg="5">
            <v-card outlined class="pa-6 rounded-lg h-100">
              <v-row>
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold">
                    {{ itemModule.item }} Size Information
                  </span>
                </v-col>
                <v-col cols="12" lg="6">
                  <InputField
                    id="length"
                    type="number"
                    step="0.0001"
                    min="0.0001"
                    name="length"
                    v-model="itemDetails.length"
                    label="Length"
                    :suffix="UOM.CM"
                    :required="true"
                    :rules="[
                      (val) => val > 0 || 'Negative Numbers are not allowed',
                    ]"
                    :error-messages="formErrors.length"
                    @input="delete formErrors.length"
                  />
                </v-col>
                <v-col cols="12" lg="6">
                  <InputField
                    id="width"
                    type="number"
                    step="0.0001"
                    min="0.0001"
                    v-model="itemDetails.width"
                    name="width"
                    label="Width"
                    :suffix="UOM.CM"
                    :required="true"
                    :rules="[
                      (val) => val > 0 || 'Negative Numbers are not allowed',
                    ]"
                    :error-messages="formErrors.width"
                    @input="delete formErrors.width"
                  />
                </v-col>
                <v-col cols="12" lg="6">
                  <InputField
                    id="height"
                    type="number"
                    step="0.0001"
                    min="0.0001"
                    v-model="itemDetails.height"
                    name="height"
                    label="Height"
                    :suffix="UOM.CM"
                    :required="true"
                    :rules="[
                      (val) => val > 0 || 'Negative Numbers are not allowed',
                    ]"
                    :error-messages="formErrors.height"
                    @input="delete formErrors.height"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>
          <v-col cols="12" lg="12">
            <v-card outlined class="pa-6 rounded-lg h-100">
              <v-row>
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold">
                    {{ itemModule.item }} Weight Information
                  </span>
                </v-col>
                <v-col cols="12" lg="4">
                  <InputField
                    id="weight"
                    type="number"
                    name="weight"
                    min="0.0001"
                    step="0.0001"
                    v-model="itemDetails.weight"
                    label="Weight"
                    :suffix="UOM.KG"
                    :required="true"
                    :rules="[
                      (val) => val > 0 || 'Negative Numbers are not allowed',
                    ]"
                    :error-messages="formErrors.weight"
                    @input="delete formErrors.weight"
                  />
                </v-col>
                <v-col cols="12" lg="4">
                  <InputField
                    id="volume"
                    type="number"
                    name="volume"
                    step="0.0001"
                    min="0.0001"
                    v-model="itemDetails.volume"
                    label="Volume"
                    :suffix="UOM.CBM"
                    :required="true"
                    :rules="[
                      (val) => val > 0 || 'Negative Numbers are not allowed',
                    ]"
                    :error-messages="formErrors.volume"
                    @input="delete formErrors.volume"
                  />
                </v-col>
                <v-col cols="12" lg="4">
                  <InputField
                    id="case_factor"
                    type="number"
                    step="0.0001"
                    name="case_factor"
                    min="0.0001"
                    v-model="itemDetails.case_factor"
                    label="Case Factor"
                    :required="true"
                    :rules="[
                      (val) => val > 0 || 'Negative Numbers are not allowed',
                    ]"
                    :error-messages="formErrors.case_factor"
                    @input="delete formErrors.case_factor"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </template>
    <template #actions>
      <SecondaryButton
        id="itemReset"
        v-if="!editMode"
        @click="resetForm()"
        btnClass="mr-2 text-uppercase rounded-lg"
        name="Reset"
      >
      </SecondaryButton>
      <PrimaryButton
        id="itemSubmit"
        @click="submitForm()"
        :disabled="!isValid"
        btnClass="text-uppercase rounded-lg"
        name="Submit"
      >
      </PrimaryButton>
    </template>
  </BaseDialogForm>
</template>

<script>
import { bus } from "@/main.js";
import BaseDialogForm from "@/components/LayoutComponents/BaseDialogForm.vue";
import { itemModule } from "@/utils/moduleMapping";
import { UOM } from "@/utils/fieldConstants";
import { itemNumberValidation } from "@/utils/functions";

export default {
  components: { BaseDialogForm },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editMode: {
      required: true,
    },
    itemID: {
      type: Number,
    },
  },
  data() {
    return {
      UOM,
      itemNumberValidation,
      itemModule,
      fileInput: null,
      formCount: 0,
      isValid: true,
      itemDetails: {},
      formErrors: {},
      unitList: [],
      storageType: [],
    };
  },
  watch: {
    value(val) {
      if (val) {
        this.getStorageTypeSelectList();
        // this.getItemOptions();
        if (this.editMode && this.itemID) {
          this.getItemObject(this.itemID);
        }
      } else {
        this.formErrors = {};
        this.resetForm();
      }
    },
  },
  computed: {
    showItemForm: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  methods: {
    // autoUpdateCbm() {
    //   if (
    //     this.itemDetails.length &&
    //     this.itemDetails.width &&
    //     this.itemDetails.height
    //   ) {
    //     this.itemDetails.volume =
    //       (
    //         this.itemDetails.length *
    //         this.itemDetails.width *
    //         this.itemDetails.height
    //       ).toFixed(3) / 1000;
    //   }
    // },
    getItemOptions() {
      this.$api.item
        .getItemOptions({ request_type: "filter" })
        .then((res) => {
          this.storageType = res?.data?.storage_type;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    resetForm() {
      this.itemDetails = {};
      this.$refs.itemForm.reset();
    },
    getItemObject(id) {
      this.$api.item
        .getItemObject(id)
        .then((res) => {
          this.itemDetails = res.data;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getStorageTypeSelectList() {
      bus.$emit("showLoader", true);
      return this.$api.storageType
        .getStorageTypeSelectList({ limit: "all" })
        .then((res) => {
          this.storageType = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.log(err);
          bus.$emit("showLoader", false);
        });
    },
    submitForm() {
      if (!this.$refs.itemForm.validate()) {
        bus.$emit("showToastMessage", {
          message: "Please correct all the errors to submit the form!",
          color: "error",
        });
        return;
      }
      if (this.editMode) {
        this.$api.item
          .updateItemObject({
            id: this.itemDetails.id,
            data: this.itemDetails,
          })
          .then(() => {
            bus.$emit("showToastMessage", {
              message: "Item information updated!",
              color: "success",
            });
            this.$emit("refreshList");
            this.showItemForm = false;
          })
          .catch((err) => {
            console.log(err);
            this.formErrors = err.data;
          });
      } else {
        this.$api.item
          .createItemObject(this.itemDetails)
          .then(() => {
            bus.$emit("showToastMessage", {
              message: "Item created!",
              color: "success",
            });
            this.showItemForm = false;
            this.$emit("refreshList");
          })
          .catch((err) => {
            this.formErrors = err.data;
          });
      }
    },
  },
};
</script>

<style>
.v-select__slot label {
  z-index: 2;
}
</style>
